/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateUserGroupRequest } from '../models/create-user-group-request';
import { GenericResponse } from '../models/generic-response';
import { GetUserGroupsResponse } from '../models/get-user-groups-response';
import { GetUsersByGroupResponse } from '../models/get-users-by-group-response';
import { UpdateUserGroupRequest } from '../models/update-user-group-request';
import { UserGroupDetailedResponse } from '../models/user-group-detailed-response';
import { UserGroupResponse } from '../models/user-group-response';

/**
 * User groups api
 */
@Injectable({ providedIn: 'root' })
export class UserGroupsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllUserGroups()` */
  static readonly GetAllUserGroupsPath = '/api/v1/user-groups';

  /**
   * Get list of user groups.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllUserGroups()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUserGroups$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name
       */
      name?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetUserGroupsResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.GetAllUserGroupsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('name', params.name, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetUserGroupsResponse>;
        })
      );
  }

  /**
   * Get list of user groups.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllUserGroups$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUserGroups(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name
       */
      name?: string;
    },
    context?: HttpContext
  ): Observable<GetUserGroupsResponse> {
    return this.getAllUserGroups$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetUserGroupsResponse>): GetUserGroupsResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createUserGroup()` */
  static readonly CreateUserGroupPath = '/api/v1/user-groups';

  /**
   * Create a new user.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createUserGroup()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createUserGroup$Response(
    params: {
      body: CreateUserGroupRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserGroupResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.CreateUserGroupPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserGroupResponse>;
        })
      );
  }

  /**
   * Create a new user.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createUserGroup$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createUserGroup(
    params: {
      body: CreateUserGroupRequest;
    },
    context?: HttpContext
  ): Observable<UserGroupResponse> {
    return this.createUserGroup$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserGroupResponse>): UserGroupResponse => r.body
      )
    );
  }

  /** Path part for operation `getAllUsersByGroup()` */
  static readonly GetAllUsersByGroupPath = '/api/v1/user-groups/{name}/users';

  /**
   * Get list of user groups.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllUsersByGroup()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUsersByGroup$Response(
    params: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - user name - user email
       */
      search?: string;

      /**
       * The user group name of which users needs to be fetched
       */
      name: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetUsersByGroupResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.GetAllUsersByGroupPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.path('name', params.name, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetUsersByGroupResponse>;
        })
      );
  }

  /**
   * Get list of user groups.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllUsersByGroup$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUsersByGroup(
    params: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - user name - user email
       */
      search?: string;

      /**
       * The user group name of which users needs to be fetched
       */
      name: string;
    },
    context?: HttpContext
  ): Observable<GetUsersByGroupResponse> {
    return this.getAllUsersByGroup$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetUsersByGroupResponse>
        ): GetUsersByGroupResponse => r.body
      )
    );
  }

  /** Path part for operation `getUserGroupById()` */
  static readonly GetUserGroupByIdPath = '/api/v1/user-groups/{id}';

  /**
   * Get user group details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUserGroupById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserGroupById$Response(
    params: {
      /**
       * The user group id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserGroupDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.GetUserGroupByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserGroupDetailedResponse>;
        })
      );
  }

  /**
   * Get user group details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUserGroupById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserGroupById(
    params: {
      /**
       * The user group id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<UserGroupDetailedResponse> {
    return this.getUserGroupById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<UserGroupDetailedResponse>
        ): UserGroupDetailedResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteUserGroup()` */
  static readonly DeleteUserGroupPath = '/api/v1/user-groups/{id}';

  /**
   * Delete user group.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteUserGroup()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUserGroup$Response(
    params: {
      /**
       * The user group id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.DeleteUserGroupPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete user group.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteUserGroup$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUserGroup(
    params: {
      /**
       * The user group id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteUserGroup$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateUserGroup()` */
  static readonly UpdateUserGroupPath = '/api/v1/user-groups/{id}';

  /**
   * Update user group by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateUserGroup()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateUserGroup$Response(
    params: {
      /**
       * The user group id that needs to be updated
       */
      id: number;
      body: UpdateUserGroupRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserGroupResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.UpdateUserGroupPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserGroupResponse>;
        })
      );
  }

  /**
   * Update user group by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateUserGroup$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateUserGroup(
    params: {
      /**
       * The user group id that needs to be updated
       */
      id: number;
      body: UpdateUserGroupRequest;
    },
    context?: HttpContext
  ): Observable<UserGroupResponse> {
    return this.updateUserGroup$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserGroupResponse>): UserGroupResponse => r.body
      )
    );
  }
}
